// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui
import { Stack } from '@mui/material';

// api
import { useGetV2AlarmsByIdQuery } from '@/api/generated/recalc/endpoints/v2/alarms';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import ElectricitySystem from '@/features/alarm-hub/components/alarmType/electricitySystem/ElectricitySystem';
import EnergyXplorerOperations from '@/features/alarm-hub/components/alarmType/energyXplorerOperations/EnergyXplorerOperations';
import ExternalSensorDown from '@/features/alarm-hub/components/alarmType/ExternalSensorDown.tsx';
import FastSensorDown from '@/features/alarm-hub/components/alarmType/FastSensorDown.tsx';
import HeatingSystem from '@/features/alarm-hub/components/alarmType/HeatingSystem';
import QualityOfServiceDataStream from '@/features/alarm-hub/components/alarmType/qualityOfServiceDataStream/QualityOfServiceDataStream';
import SeepLeakage from '@/features/alarm-hub/components/alarmType/seepLeakage/SeepLeakage';
import SensorDown from '@/features/alarm-hub/components/alarmType/SensorDown.tsx';
import ThresholdLimit from '@/features/alarm-hub/components/alarmType/thresholdLimit/ThresholdLimit';
import UsageOverTime from '@/features/alarm-hub/components/alarmType/usageOverTime/UsageOverTime';

export const Route = createFileRoute('/$accountId/_user/alarms/$alarmId')({
  component: RouteComponent,
  staticData: { crumb: 'alarms:detail.title' },
});

function toLegacyAlarmShape(alarm) {
  if (!alarm) return null;

  return {
    AlarmId: alarm.alarmId,
    AccountId: alarm.accountId,
    AlarmType: alarm.alarmType,
    DataFrom: alarm.dataFrom,
    DataTo: alarm.dataTo,
    AlarmSubtype: alarm.alarmSubtype,
    Severity: alarm.severity,
    Title: alarm.title,
    Message: alarm.message,
    MetaData: alarm.metaData,
    Details: alarm.details,
    State: alarm.state,
    Created: alarm.created,
    StateChanged: alarm.stateChanged,
    StateHistory: alarm.stateHistory,
    Associations: {
      Units:
        alarm.associations?.units
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'number') || [],
      Buildings:
        alarm.associations?.buildings
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'number') || [],
      JobSubscriptions:
        alarm.associations?.jobSubscriptions
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'number') || [],
      Sensors:
        alarm.associations?.sensors
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'number') || [],
      Gateways:
        alarm.associations?.gateways
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'number') || [],
      EnergyGroups:
        alarm.associations?.energyGroups
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'number') || [],
      EnergyInitiatives:
        alarm.associations?.energyInitiatives
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'number') || [],
      EnergyTreeNodes:
        alarm.associations?.energyTreeNodes
          ?.map((item) => item?.id)
          .filter((id) => typeof id === 'string') || [],
      DataStreams: alarm.associations?.dataStreams || [],
    },
  };
}

function renderAlarmType(alarmType, alarm) {
  switch (alarmType) {
    case 'HeatingSystem':
      return <HeatingSystem alarm={alarm} />;
    case 'SeepLeakage':
      return <SeepLeakage alarm={alarm} />;
    case 'EnergyXOperations':
      return <EnergyXplorerOperations alarm={alarm} />;
    case 'ElectricitySystem':
      return <ElectricitySystem alarm={alarm} />;
    case 'SensorDown':
      return <SensorDown alarm={alarm} />;
    case 'FastSensorDown':
      return <FastSensorDown alarm={alarm} />;
    case 'ExternalSensorDown':
      return <ExternalSensorDown alarm={alarm} />;
    case 'ThresholdLimit':
      return <ThresholdLimit alarm={alarm} />;
    case 'UsageOverTime':
      return <UsageOverTime alarm={alarm} />;
    case 'QualityOfServiceDataStream':
      return <QualityOfServiceDataStream alarm={alarm} />;
    default:
      return alarmType;
  }
}

function RouteComponent() {
  const { alarmId, accountId } = useParams({ strict: false });

  const { t } = useTranslation('alarms');

  const parsedAlarmId = Number(alarmId);
  const parsedAccountId = Number(accountId);

  const { data: alarmResponse } = useGetV2AlarmsByIdQuery(
    {
      id: parsedAlarmId,
      accountIds: Number.isFinite(parsedAccountId) ? [parsedAccountId] : [],
    },
    {
      skip: !Number.isFinite(parsedAlarmId),
    },
  );

  const alarm = toLegacyAlarmShape(alarmResponse);

  const alarmType = alarm?.AlarmType;

  console.log('alarm', alarm);

  if (!alarm) return null;
  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader title={t('detail.title')} />
      <Stack spacing={2}>{renderAlarmType(alarmType, alarm)}</Stack>
    </PageContainer>
  );
}
